import { useEffect, useState } from "react"
import { Platform, View } from "react-native"

import { Dialog as ThemedDialog } from "@rn-vui/themed"
import { useRouter } from "expo-router"

import { Button, Text } from "src/components/Ignite"
import { useAuth } from "src/context/AuthContext"
import { useAppTheme } from "src/hooks/useAppTheme"

/**
 * Shown when this client was displaced by another login (WS 4001 or HTTP SESSION_DEACTIVATED).
 *
 * Logout button is intentionally hidden on web. On web, "Log out" runs through
 * `localLogoutAfterDeactivation` → `clearAllTemporaryData()` which clears the
 * shared per-user MMKV (Cognito tokens, defaultApiHost) — meaning logging out
 * from one tab's takeover dialog also signs every other tab out, which is
 * never what the user wants in the multi-tab takeover scenario. The native
 * app is single-tab so the button is still useful there.
 */
export function SessionTakeoverDialog() {
  const { theme } = useAppTheme()
  const router = useRouter()
  const { isSessionDeactivated, isSponsorInactive, resumeSession, localLogoutAfterDeactivation } =
    useAuth()
  const [resumeError, setResumeError] = useState<string | null>(null)
  const [busy, setBusy] = useState<"resume" | "logout" | null>(null)
  const showLogout = Platform.OS !== "web"
  // The terminal sponsor-inactive dialog supersedes the takeover dialog: a
  // racing WS 4001 close may have set isSessionDeactivated before the HTTP 401
  // classified the channel as gone. Never show "Resume Here" for a dead channel.
  const isVisible = isSessionDeactivated && !isSponsorInactive

  useEffect(() => {
    if (isSessionDeactivated) {
      setResumeError(null)
    }
  }, [isSessionDeactivated])

  const onResume = async () => {
    setResumeError(null)
    setBusy("resume")
    try {
      const r = await resumeSession()
      if (r.kind === "error") {
        setResumeError(
          showLogout
            ? "Could not resume this session. Please try again or log out."
            : "Could not resume this session. Please try again.",
        )
      }
    } finally {
      setBusy(null)
    }
  }

  const onLogout = () => {
    setBusy("logout")
    localLogoutAfterDeactivation()
    router.replace("/unauth/login")
  }

  return (
    <ThemedDialog
      isVisible={isVisible}
      onBackdropPress={() => {}}
      overlayStyle={{
        backgroundColor: theme.colors.background,
        borderRadius: 12,
        padding: 24,
        maxWidth: 400,
        width: "90%",
      }}
    >
      <ThemedDialog.Title
        title="Another session active"
        titleStyle={{
          color: theme.colors.text,
          fontWeight: "600",
          fontSize: 18,
          marginBottom: 12,
        }}
      />
      <Text style={{ marginBottom: 16, color: theme.colors.text }}>
        {showLogout
          ? "You've started using this Sidekick channel on another device or browser. Do you want to:"
          : "You've started using this Sidekick channel in another tab or browser. Resume your session in this tab?"}
      </Text>
      {resumeError ? (
        <Text style={{ marginBottom: 12, color: theme.colors.error }}>{resumeError}</Text>
      ) : null}
      <View style={{ flexDirection: "row", gap: 12, flexWrap: "wrap" }}>
        <View style={{ flex: 1, minWidth: 120 }}>
          <Button
            text="Resume Here"
            preset="reversed"
            onPress={onResume}
            disabled={busy !== null}
          />
        </View>
        {showLogout ? (
          <View style={{ flex: 1, minWidth: 120 }}>
            <Button text="Log out" preset="subtle" onPress={onLogout} disabled={busy !== null} />
          </View>
        ) : null}
      </View>
    </ThemedDialog>
  )
}
